/**
 * 챗봇 응답 서비스 — mock LLM ↔ BE LLM 어댑터.
 *
 * BE 연동 시:
 *   1. setChatAdapter(createBackendAdapter(apiClient)) — boot 시 1회
 *   2. 컴포넌트는 import { askInnerCoach } 그대로 사용
 *
 * 회의록 14:05 — RAG는 BE에서 구축, FE는 stream/non-stream 응답만 받음.
 * 첫 버전은 non-stream (전체 JSON 반환), W3에 SSE/streaming 검토.
 */

import { AICardData, aiRespond as mockAiRespond } from './mockResponses';

// AICardData를 service에서도 export (컴포넌트는 service에서만 가져가도록)
export type { AICardData } from './mockResponses';

// ============== Public types ==============

export interface AskInput {
  /** 사용자 자유 입력 또는 chip의 prompt */
  text: string;
  /** 호출 컨텍스트 (chip 클릭 vs 자유 입력 vs 사진 분석 후) */
  source: 'free' | 'chip' | 'photo-followup';
  /** AbortSignal — 컴포넌트 unmount 시 취소 */
  signal?: AbortSignal;
}

export interface AskOutput {
  /** AI 자연어 응답 (다중 — 클라이언트가 순차 표시) */
  messages: string[]; // <strong>, <br> HTML 허용
  /** 인사이트 카드 (optional) */
  card?: AICardData;
  /** 다음 추천 chip (BE가 컨텍스트에 맞게 정렬) */
  suggestedChips?: Array<{ id: string; label: string; prompt: string }>;
}

export interface ChatAdapter {
  ask(input: AskInput): Promise<AskOutput>;
}

// ============== Mock adapter (현재 default) ==============

export function createMockAdapter(): ChatAdapter {
  return {
    async ask({ text, signal }) {
      // 800~1100ms 지연으로 실제 LLM 느낌
      const delay = 800 + Math.random() * 300;
      await waitWithAbort(delay, signal);
      const res = mockAiRespond(text);
      return {
        messages: res.messages,
        card: res.card,
      };
    },
  };
}

// ============== Backend adapter (BE 연결 시 사용) ==============

/**
 * BE 챗봇 어댑터 — 케어 코치 API 연동
 *
 * BE 계약: POST /v1/health/ctbt/message
 *   요청  { sndgTypeCd, msgCntnt }
 *   응답  { messages: string[], card?: CtbtCard }
 * BE는 현재 임시 목 데이터를 반환한다(추후 실제 AI로 교체, 계약 유지).
 */
import { CtbtCard } from '../chatbot';
import { chatbotApi } from '../index';

/** BE CtbtCard → FE AICardData (action/topic 의 null 을 undefined 로 정규화) */
function toCardData(card?: CtbtCard | null): AICardData | undefined {
  if (!card) return undefined;
  return {
    header: card.header,
    title: card.title,
    desc: card.desc,
    actions: card.actions?.map((a) => ({
      label: a.label,
      primary: a.primary,
      action: a.action ?? undefined,
      topic: a.topic ?? undefined,
    })),
  };
}

export function createBackendAdapter(): ChatAdapter {
  return {
    async ask({ text, signal }) {
      try {
        // 사용자 메시지 전송 → 인텐트 매칭 응답(messages + card)
        const result = await chatbotApi.sendChatbotMessage(text);

        if (result?.resultCode === 'S' && result.data) {
          return {
            messages: result.data.messages ?? [],
            card: toCardData(result.data.card),
          };
        }

        throw new ChatError('Failed to get response', 500);
      } catch (error) {
        if (signal?.aborted) {
          throw new ChatError('Request aborted', 0);
        }
        console.error('[Chat] Failed to ask:', error);
        throw new ChatError('Failed to get response', 500);
      }
    },
  };
}

// ============== Adapter registry (swap 가능) ==============

let _adapter: ChatAdapter = createMockAdapter();

export function setChatAdapter(adapter: ChatAdapter): void {
  _adapter = adapter;
}

export function askInnerCoach(input: AskInput): Promise<AskOutput> {
  return _adapter.ask(input);
}

// ============== Error ==============

export class ChatError extends Error {
  public readonly status?: number;
  public readonly statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'ChatError';
    this.statusCode = statusCode;
    this.status = statusCode;
  }
}

// ============== Helpers ==============

function waitWithAbort(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}
